'use client'

import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'
import { Button } from '../Button'

const variantConfig = {
  info: {
    icon: InformationCircleIcon,
    iconColor: 'text-[var(--color-cyan)]',
    iconBg: 'bg-[var(--color-cyan-muted)]',
  },
  success: {
    icon: CheckCircleIcon,
    iconColor: 'text-[var(--color-green)]',
    iconBg: 'bg-[var(--color-green-muted)]',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-[var(--color-orange)]',
    iconBg: 'bg-[var(--color-orange-muted)]',
  },
  danger: {
    icon: XCircleIcon,
    iconColor: 'text-[var(--color-pink)]',
    iconBg: 'bg-[var(--color-pink-muted)]',
  },
} as const

export interface PopupProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string | React.ReactNode
  variant?: keyof typeof variantConfig
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
  showCancel?: boolean
  loading?: boolean
  className?: string
}

const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = true,
  loading = false,
  className,
}) => {
  const config = variantConfig[variant]
  const IconComponent = config.icon

  const handleConfirm = () => {
    onConfirm?.()
    if (!loading) onClose()
  }

  const handleCancel = () => {
    onCancel?.()
    onClose()
  }

  const confirmVariant = variant === 'danger' ? 'destructive' : variant === 'success' ? 'default' : variant === 'warning' ? 'cyan' : 'default'

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn(
                  'w-full max-w-md transform overflow-hidden rounded-2xl p-6 text-left align-middle shadow-xl transition-all',
                  className
                )}
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={cn('p-3 rounded-full mb-4', config.iconBg)}>
                    <IconComponent className={cn('h-8 w-8', config.iconColor)} />
                  </div>

                  <Dialog.Title as="h3" className="text-lg font-semibold text-foreground">
                    {title}
                  </Dialog.Title>

                  <div className="mt-2 text-sm text-muted-foreground">
                    {typeof message === 'string' ? <p>{message}</p> : message}
                  </div>

                  <div className="mt-6 flex gap-3 w-full">
                    {showCancel && (
                      <Button
                        variant="secondary"
                        className="flex-1"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        {cancelLabel}
                      </Button>
                    )}
                    <Button
                      variant={confirmVariant}
                      className="flex-1"
                      onClick={handleConfirm}
                      loading={loading}
                    >
                      {confirmLabel}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export { Popup }
